/**
 * Vector file format converter utility.
 * Handles EPS→SVG and AI→SVG conversion via external APIs (CloudConvert/Zamzar),
 * and SVG→raster conversion for logo preview/texture use.
 */

// ============= Configuration =============

interface ConversionApiConfig {
    provider: 'cloudconvert' | 'zamzar';
    apiKey: string;
    /** Optional sandbox mode for CloudConvert */
    sandbox?: boolean;
}

// Default config — Zamzar is the primary provider
let apiConfig: ConversionApiConfig = {
    provider: 'zamzar',
    apiKey: import.meta.env.VITE_ZAMZAR_API_KEY || '',
};

/**
 * Update the conversion API configuration at runtime.
 */
export function setConversionApiConfig(config: Partial<ConversionApiConfig>) {
    apiConfig = { ...apiConfig, ...config };
}

// ============= Format Detection =============

export type VectorFormat = 'svg' | 'eps' | 'ai';
export type UploadFormat = VectorFormat | 'raster';

/**
 * Detect the format of a file based on extension and MIME type.
 */
export function detectFileFormat(file: File): UploadFormat {
    const name = file.name.toLowerCase();
    if (name.endsWith('.svg')) return 'svg';
    if (name.endsWith('.eps') || name.endsWith('.epsf')) return 'eps';
    if (name.endsWith('.ai')) return 'ai';
    // Check MIME type as fallback
    if (file.type === 'image/svg+xml') return 'svg';
    if (file.type === 'application/postscript' || file.type === 'application/eps') return 'eps';
    return 'raster';
}

// ============= SVG Utilities =============

/**
 * Read an SVG file as text string.
 */
export async function readSvgFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = () => reject(new Error('Failed to read SVG file'));
        reader.readAsText(file);
    });
}

/**
 * Read a file as base64 data URL string.
 */
export async function readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
}

/**
 * Rasterize an SVG string into a PNG data URL for use as logo preview/texture.
 * Returns the data URL and intrinsic dimensions.
 */
export async function rasterizeSvg(
    svgString: string,
    maxSize: number = 1024
): Promise<{ dataUrl: string; width: number; height: number }> {
    return new Promise((resolve, reject) => {
        // Parse SVG to extract viewBox/dimensions
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');
        const svgEl = svgDoc.documentElement;

        // Get intrinsic size
        let svgWidth = parseFloat(svgEl.getAttribute('width') || '0');
        let svgHeight = parseFloat(svgEl.getAttribute('height') || '0');

        // Fallback to viewBox
        if (!svgWidth || !svgHeight) {
            const viewBox = svgEl.getAttribute('viewBox');
            if (viewBox) {
                const parts = viewBox.split(/[\s,]+/).map(Number);
                if (parts.length === 4) {
                    svgWidth = parts[2];
                    svgHeight = parts[3];
                }
            }
        }

        // Default if still no dimensions
        if (!svgWidth) svgWidth = 512;
        if (!svgHeight) svgHeight = 512;

        // Scale to fit within maxSize while preserving aspect ratio
        const scale = Math.min(maxSize / svgWidth, maxSize / svgHeight, 1);
        const renderWidth = Math.round(svgWidth * scale);
        const renderHeight = Math.round(svgHeight * scale);

        // Create blob URL for the SVG
        const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);

        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = renderWidth;
            canvas.height = renderHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                URL.revokeObjectURL(url);
                reject(new Error('Could not get canvas context'));
                return;
            }

            ctx.drawImage(img, 0, 0, renderWidth, renderHeight);
            const dataUrl = canvas.toDataURL('image/png');

            URL.revokeObjectURL(url);
            resolve({
                dataUrl,
                width: Math.round(svgWidth),
                height: Math.round(svgHeight),
            });
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to rasterize SVG'));
        };
        img.src = url;
    });
}

// ============= CloudConvert API =============

/**
 * Convert a file (EPS/AI) to SVG using CloudConvert API.
 * https://cloudconvert.com/api/v2
 */
async function convertWithCloudConvert(file: File, inputFormat: 'eps' | 'ai'): Promise<string> {
    const baseUrl = apiConfig.sandbox
        ? 'https://api.sandbox.cloudconvert.com'
        : 'https://api.cloudconvert.com';

    // Step 1: Create a job with upload + convert + export tasks
    const jobResponse = await fetch(`${baseUrl}/v2/jobs`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${apiConfig.apiKey}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            tasks: {
                'upload-file': {
                    operation: 'import/upload',
                },
                'convert-file': {
                    operation: 'convert',
                    input: ['upload-file'],
                    input_format: inputFormat,
                    output_format: 'svg',
                },
                'export-file': {
                    operation: 'export/url',
                    input: ['convert-file'],
                },
            },
        }),
    });

    if (!jobResponse.ok) {
        const err = await jobResponse.text();
        throw new Error(`CloudConvert job creation failed: ${err}`);
    }

    const jobData = await jobResponse.json();
    const jobId = jobData.data.id;

    // Step 2: Upload the file to the upload task
    const uploadTask = jobData.data.tasks.find((t: any) => t.name === 'upload-file');
    if (!uploadTask?.result?.form) {
        throw new Error('CloudConvert: No upload form received');
    }

    const formData = new FormData();
    // Add all form parameters
    for (const [key, value] of Object.entries(uploadTask.result.form.parameters)) {
        formData.append(key, value as string);
    }
    formData.append('file', file);

    const uploadResponse = await fetch(uploadTask.result.form.url, {
        method: 'POST',
        body: formData,
    });

    if (!uploadResponse.ok) {
        throw new Error('CloudConvert: File upload failed');
    }

    // Step 3: Poll for job completion
    let attempts = 0;
    const maxAttempts = 60; // 60 * 2s = 2 minutes max wait

    while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        attempts++;

        const statusResponse = await fetch(`${baseUrl}/v2/jobs/${jobId}`, {
            headers: { 'Authorization': `Bearer ${apiConfig.apiKey}` },
        });

        if (!statusResponse.ok) continue;

        const statusData = await statusResponse.json();
        const job = statusData.data;

        if (job.status === 'finished') {
            // Find the export task and get the download URL
            const exportTask = job.tasks.find((t: any) => t.name === 'export-file');
            const downloadUrl = exportTask?.result?.files?.[0]?.url;

            if (!downloadUrl) {
                throw new Error('CloudConvert: No download URL in completed job');
            }

            // Fetch the SVG content
            const svgResponse = await fetch(downloadUrl);
            if (!svgResponse.ok) {
                throw new Error('CloudConvert: Failed to download SVG result');
            }

            return await svgResponse.text();
        }

        if (job.status === 'error') {
            const errorTask = job.tasks.find((t: any) => t.status === 'error');
            throw new Error(`CloudConvert conversion failed: ${errorTask?.message || 'Unknown error'}`);
        }
    }

    throw new Error('CloudConvert: Conversion timed out');
}

// ============= Zamzar API =============

/**
 * Convert a file (EPS/AI) to SVG using Zamzar API.
 * https://developers.zamzar.com/
 */
async function convertWithZamzar(file: File, _inputFormat: 'eps' | 'ai'): Promise<string> {
    // Use local Vite proxy to avoid CORS
    const baseUrl = '/zamzar-api';
    const authHeader = 'Basic ' + btoa(apiConfig.apiKey + ':');
    
    // We send X-Zamzar-Auth instead of Authorization.
    // Nginx will inject this as the real Authorization header to Zamzar.
    // This brilliantly prevents the browser from blindly forwarding the basic auth 
    // to Amazon S3 when Zamzar issues a 302 Redirection for the actual file, 
    // saving us from S3's strict '403 Forbidden' header policy!
    const customHeaders = { 'X-Zamzar-Auth': authHeader };

    // Step 1: Upload and start conversion
    const formData = new FormData();
    formData.append('source_file', file);
    formData.append('target_format', 'svg');

    const jobResponse = await fetch(`${baseUrl}/jobs`, {
        method: 'POST',
        headers: customHeaders,
        body: formData,
    });

    if (!jobResponse.ok) {
        const err = await jobResponse.text();
        throw new Error(`Zamzar job creation failed: ${err}`);
    }

    const jobData = await jobResponse.json();
    const jobId = jobData.id;

    // Step 2: Poll for completion
    let attempts = 0;
    const maxAttempts = 60; // 60 * 5s = 5 minutes max wait

    while (attempts < maxAttempts) {
        // Zamzar free tier enforces a strict 30 requests per minute limit.
        // We poll every 5 seconds (12/min) to stay well under the 403 limit.
        await new Promise(resolve => setTimeout(resolve, 5000));
        attempts++;

        const statusResponse = await fetch(`${baseUrl}/jobs/${jobId}`, {
            headers: customHeaders,
        });

        if (!statusResponse.ok) continue;

        const statusData = await statusResponse.json();

        if (statusData.status === 'successful') {
            const fileId = statusData.target_files?.[0]?.id;
            if (!fileId) {
                throw new Error('Zamzar: No output file in completed job');
            }

            // Download the converted file
            const downloadResponse = await fetch(`${baseUrl}/files/${fileId}/content`, {
                headers: customHeaders,
            });

            if (!downloadResponse.ok) {
                throw new Error('Zamzar: Failed to download SVG result');
            }

            return await downloadResponse.text();
        }

        if (statusData.status === 'failed') {
            throw new Error(`Zamzar conversion failed: ${statusData.error || 'Unknown error'}`);
        }
    }

    throw new Error('Zamzar: Conversion timed out');
}

// ============= Public API =============

/**
 * Convert an EPS or AI file to SVG using the configured API provider.
 * Returns the SVG content as a string.
 */
export async function convertToSvg(file: File, format: 'eps' | 'ai'): Promise<string> {
    if (!apiConfig.apiKey) {
        throw new Error(
            'No conversion API key configured. Set VITE_ZAMZAR_API_KEY in your .env file, ' +
            'or call setConversionApiConfig() with your API key.'
        );
    }

    if (apiConfig.provider === 'zamzar') {
        return convertWithZamzar(file, format);
    }

    // Default: CloudConvert
    return convertWithCloudConvert(file, format);
}

/**
 * Process any supported file into logo data.
 * Returns the data needed to create a Logo object.
 */
export async function processVectorFile(file: File): Promise<{
    dataUrl: string;
    width: number;
    height: number;
    sourceType: 'raster' | 'svg' | 'eps' | 'ai';
    svgContent?: string;
    sourceFile?: string;
    sourceFileName: string;
}> {
    const format = detectFileFormat(file);

    if (format === 'svg') {
        // Read SVG content directly
        const svgContent = await readSvgFile(file);
        
        // Skip rasterization! Directly base64 encode the SVG for infinitely crisp vectors!
        const b64 = btoa(unescape(encodeURIComponent(svgContent)));
        const dataUrl = `data:image/svg+xml;base64,${b64}`;
        
        // We still extract intrinsic dimensions to place it properly on the canvas
        const { width, height } = await getSvgDimensions(dataUrl);

        return {
            dataUrl,
            width,
            height,
            sourceType: 'svg',
            svgContent,
            sourceFileName: file.name,
        };
    }

    if (format === 'eps' || format === 'ai') {
        // Store original file as base64 for pass-through download
        const sourceFile = await readFileAsBase64(file);

        // Convert to SVG via API
        const svgContent = await convertToSvg(file, format);
        
        // Skip rasterization! Directly base64 encode the SVG for infinitely crisp vectors!
        const b64 = btoa(unescape(encodeURIComponent(svgContent)));
        const dataUrl = `data:image/svg+xml;base64,${b64}`;
        
        const { width, height } = await getSvgDimensions(dataUrl);

        return {
            dataUrl,
            width,
            height,
            sourceType: format,
            svgContent,
            sourceFile,
            sourceFileName: file.name,
        };
    }

    // Raster file — return dataUrl directly
    const dataUrl = await readFileAsBase64(file);
    
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            resolve({
                dataUrl,
                width: img.width,
                height: img.height,
                sourceType: 'raster',
                sourceFileName: file.name,
            });
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = dataUrl;
    });
}

/**
 * Utility to extract intrinsic dimensions from a crisp SVG data URL
 */
async function getSvgDimensions(dataUrl: string): Promise<{width: number, height: number}> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.width || 512, height: img.height || 512 });
        img.onerror = () => reject(new Error('Failed to load SVG dimensions'));
        img.src = dataUrl;
    });
}
